/**
 * J7CelebrationModal — Iter. 406.h
 *
 * Emotional close-loop modal triggered when the user completes their 7-day
 * Welcome Challenge by uploading their 3 transformation photos (face, profile,
 * dos). It frames the "Avant YUMIX" moment and projects forward to the 6-week
 * cycle reassessment (Iter. 405.j) where the "Après" photos will be taken.
 *
 * Trigger: parent component (Evolution.js after photo upload, or Dashboard
 * after J7 check) sets `open=true` and `userId`. The modal:
 *   1. Persists localStorage key `yumix_j7_celebration_seen_<userId>` on close
 *      so it never re-shows for the same user.
 *   2. Shows a CTA → /evolution (open=their gallery, but if already on
 *      /evolution we just dismiss).
 *
 * Why this matters:
 *   • J7 (Iter. 406.e) is the "Avant YUMIX" photo capture moment.
 *   • Cycle 6-week (Iter. 405.j) is the "Après YUMIX" moment with PDF report
 *     (Iter. 405.k).
 *   • This modal bridges the 2 — reinforces commitment, kicks dopamine, and
 *     plants the seed of the upcoming transformation.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Camera, Calendar, ArrowRight, X } from 'lucide-react';
import useCameraViewport from '../utils/useCameraViewport';

const SEEN_KEY_PREFIX = 'yumix_j7_celebration_seen_';

export function hasSeenJ7Celebration(userId) {
  if (!userId || typeof window === 'undefined') return true;
  try {
    return !!window.localStorage.getItem(`${SEEN_KEY_PREFIX}${userId}`);
  } catch {
    return false;
  }
}

export function markJ7CelebrationSeen(userId) {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${SEEN_KEY_PREFIX}${userId}`, new Date().toISOString());
  } catch {
    /* ignore quota errors */
  }
}

export default function J7CelebrationModal({
  open,
  onClose,
  userId,
  userName = '',
  language = 'fr',
  alreadyOnEvolution = false,
}) {
  const fr = language === 'fr';
  const navigate = useNavigate();
  const [closing, setClosing] = useState(false);

  // Iter. 406.d hook — full-viewport iOS-safe shell, body scroll locked.
  // The modal is a centered card on a black backdrop, so we keep
  // overflow:hidden (default).
  useCameraViewport({ open, lockBodyScroll: true });

  useEffect(() => {
    if (open) setClosing(false);
  }, [open]);

  if (!open) return null;

  const firstName = (userName || '').trim().split(' ')[0];

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    markJ7CelebrationSeen(userId);
    onClose?.();
  };

  const handleCTA = () => {
    markJ7CelebrationSeen(userId);
    if (!alreadyOnEvolution) navigate('/evolution');
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[2147483600] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
      style={{
        paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))',
      }}
      data-testid="j7-celebration-modal"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className="relative w-full max-w-sm rounded-2xl bg-gradient-to-br from-amber-500/25 via-orange-500/20 to-violet-500/25 border-2 border-amber-400/50 shadow-[0_0_60px_rgba(251,191,36,0.35)] overflow-hidden"
        data-testid="j7-celebration-card"
      >
        {/* Close X — also reachable on iPhone */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Fermer"
          data-testid="j7-celebration-close"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/50 hover:bg-black/80 text-white flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Ambient glow */}
        <div className="pointer-events-none absolute -top-20 -right-20 w-48 h-48 rounded-full bg-amber-400/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-20 w-48 h-48 rounded-full bg-violet-500/30 blur-3xl" />

        <div className="relative px-6 pt-7 pb-6 text-center">
          {/* Trophy icon */}
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-400/30 border border-amber-300/50 mb-4">
            <Trophy className="w-8 h-8 text-amber-300" />
          </div>

          {/* Title */}
          <h2 className="font-heading text-2xl uppercase tracking-tight text-white leading-tight">
            {fr ? `Bravo${firstName ? `, ${firstName}` : ''} !` : `Way to go${firstName ? `, ${firstName}` : ''}!`}
          </h2>
          <p className="text-xs uppercase tracking-[0.18em] text-amber-300/90 mt-1.5">
            {fr ? 'Defi 7 jours · J7 valide' : '7-day challenge · J7 complete'}
          </p>

          {/* Avant / Apres timeline */}
          <div className="mt-5 flex items-center justify-center gap-2">
            <div className="flex-1 max-w-[110px] rounded-xl border-2 border-amber-400/40 bg-amber-500/10 px-2 py-2.5">
              <Camera className="w-5 h-5 text-amber-300 mx-auto mb-1" />
              <p className="text-[10px] uppercase font-bold tracking-wider text-amber-200">
                {fr ? 'Avant YUMIX' : 'Before YUMIX'}
              </p>
              <p className="text-[9px] text-amber-200/70 mt-0.5">
                {fr ? 'En boite ✓' : 'Captured ✓'}
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-violet-300 shrink-0" />
            <div className="flex-1 max-w-[110px] rounded-xl border-2 border-violet-400/40 bg-violet-500/10 px-2 py-2.5">
              <Calendar className="w-5 h-5 text-violet-300 mx-auto mb-1" />
              <p className="text-[10px] uppercase font-bold tracking-wider text-violet-200">
                {fr ? '6 semaines' : '6 weeks'}
              </p>
              <p className="text-[9px] text-violet-200/70 mt-0.5">
                {fr ? 'Transformation' : 'Transformation'}
              </p>
            </div>
          </div>

          {/* Body copy */}
          <div className="mt-5 space-y-2.5 text-sm text-white/85 leading-relaxed">
            <p>
              {fr
                ? 'Tes photos AVANT YUMIX sont en boite. Tu viens de poser le premier jalon de ta transformation.'
                : 'Your BEFORE YUMIX photos are locked in. You just laid the first marker of your transformation.'}
            </p>
            <p className="text-white/70 text-[13px]">
              {fr
                ? 'Dans 6 semaines, ton cycle se complete : on prend les nouvelles photos, on compile ton bilan EXTRA MILES, et tu telecharges ton rapport PDF.'
                : "In 6 weeks, your cycle wraps: we take the new photos, we compile your EXTRA MILES report, and you grab the PDF."}
            </p>
          </div>

          {/* CTA */}
          <button
            type="button"
            onClick={handleCTA}
            data-testid="j7-celebration-cta"
            className="mt-6 w-full h-12 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-heading uppercase tracking-wider text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-lg"
          >
            <Camera className="w-4 h-4" />
            {fr ? 'Voir mon Coffre Evolution' : 'Open my Evolution Vault'}
            <ArrowRight className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={handleClose}
            data-testid="j7-celebration-later"
            className="mt-3 text-xs text-white/60 hover:text-white/90 uppercase tracking-wider"
          >
            {fr ? 'Plus tard' : 'Later'}
          </button>
        </div>
      </div>
    </div>
  );
}
